/*
 * Copyright 2024 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Text } from '../../Text';
import { Link } from '../../Link';
import { Cell as ReactAriaCell } from 'react-aria-components';
import type { CellTextProps } from '../types';
import { useDefinition } from '../../../hooks/useDefinition';
import { CellTextDefinition } from '../definition';

/**
 * A table cell that renders a primary text title with an optional secondary description, leading icon, and link.
 *
 * @public
 */
const CellText = (props: CellTextProps) => {
  const { ownProps, restProps } = useDefinition(CellTextDefinition, props);
  const { classes, title, description, color, leadingIcon, href } = ownProps;

  return (
    <ReactAriaCell className={classes.root} {...restProps}>
      <div className={classes.cellContentWrapper}>
        {leadingIcon && <div className={classes.cellIcon}>{leadingIcon}</div>}
        <div className={classes.cellContent}>
          {href ? (
            <Link
              href={href}
              variant="body-medium"
              color={color}
              truncate
              title={title}
            >
              {title}
            </Link>
          ) : (
            <Text
              as="p"
              variant="body-medium"
              color={color}
              truncate
              title={title}
            >
              {title}
            </Text>
          )}
          {description && (
            <Text
              variant="body-medium"
              color="secondary"
              truncate
              title={description}
            >
              {description}
            </Text>
          )}
        </div>
      </div>
    </ReactAriaCell>
  );
};

CellText.displayName = 'CellText';

export { CellText };
